import { Trash2, Upload } from 'lucide-react'
import { useRef, useState } from 'react'

import { UploadedFileRow } from './uploaded-file-row'
import { type UploadedDocument, useDocumentUpload } from './use-document-upload'

export type FileDropZoneValue = {
  documentId: string
  fileName: string
} | null

type FileDropZoneProps = {
  accept: string
  maxBytes: number
  value: FileDropZoneValue
  onUploaded: (document: UploadedDocument) => void
  onRemove?: () => void
  onView?: () => void
  label?: string
  disabled?: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function FileDropZone({
  accept,
  maxBytes,
  value,
  onUploaded,
  onRemove,
  onView,
  label,
  disabled = false
}: FileDropZoneProps) {
  const { upload, status, error, reset } = useDocumentUpload()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const uploading =
    status === 'requesting' || status === 'uploading' || status === 'completing'

  async function handleFile(file: File) {
    setLocalError(null)
    reset()
    const acceptList = accept.split(',').map(item => item.trim())
    if (!acceptList.includes(file.type)) {
      setLocalError(`File type ${file.type || 'unknown'} is not allowed`)
      return
    }
    if (file.size > maxBytes) {
      setLocalError(`File exceeds ${formatBytes(maxBytes)}`)
      return
    }
    try {
      const result = await upload(file)
      onUploaded(result)
    } catch {
      // error already surfaced via useDocumentUpload state
    }
  }

  function onPickClick() {
    if (disabled || uploading) return
    inputRef.current?.click()
  }

  function onInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) void handleFile(file)
    event.target.value = ''
  }

  function onDrop(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setDragOver(false)
    if (disabled || uploading) return
    const file = event.dataTransfer.files[0]
    if (file) void handleFile(file)
  }

  function onDragOver(event: React.DragEvent<HTMLDivElement>) {
    event.preventDefault()
    if (disabled || uploading) return
    setDragOver(true)
  }

  function onDragLeave() {
    setDragOver(false)
  }

  const message = localError ?? error

  const trashButton = onRemove ? (
    <button
      type='button'
      onClick={onRemove}
      disabled={disabled}
      aria-label={label ? `Remove ${label}` : 'Remove'}
      className='grid size-8 shrink-0 place-items-center rounded-md text-ink-muted transition-colors hover:bg-red-50 hover:text-red-700 disabled:opacity-50 dark:hover:bg-red-950/40 dark:hover:text-red-300'
    >
      <Trash2 className='size-3.5' />
    </button>
  ) : null

  if (value && !uploading) {
    return (
      <div className='flex flex-col gap-1'>
        <UploadedFileRow
          fileName={value.fileName}
          label={label}
          onView={onView}
          onReplace={onPickClick}
          onRemove={onRemove}
          disabled={disabled}
        />
        <input
          ref={inputRef}
          type='file'
          accept={accept}
          className='hidden'
          onChange={onInputChange}
        />
        {message && <p className='text-[12px] text-red-600'>{message}</p>}
      </div>
    )
  }

  return (
    <div className='flex flex-col gap-1'>
      <div
        role='button'
        tabIndex={disabled ? -1 : 0}
        onClick={onPickClick}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onPickClick()
          }
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        aria-disabled={disabled || uploading}
        className={`flex min-h-14 cursor-pointer flex-col items-start gap-1 rounded-xl border border-dashed px-4 py-3 transition-colors sm:min-h-0 sm:flex-row sm:items-center sm:gap-3 ${
          dragOver
            ? 'border-accent bg-accent-light/30'
            : 'border-border bg-page hover:border-accent/40 hover:bg-cream/40'
        } ${disabled || uploading ? 'pointer-events-none opacity-60' : ''}`}
      >
        {label && (
          <span className='shrink-0 text-[14px] font-semibold text-ink'>
            {label}
          </span>
        )}
        <div className='flex w-full min-w-0 flex-1 items-center gap-3'>
          <Upload className='size-5 shrink-0 text-ink-muted' />
          <p className='min-w-0 flex-1 text-[13px] text-ink-soft'>
            {uploading ? (
              status === 'uploading' ? (
                'Uploading…'
              ) : status === 'requesting' ? (
                'Preparing upload…'
              ) : (
                'Verifying…'
              )
            ) : (
              <>
                <span className='sm:hidden'>Tap to upload a file</span>
                <span className='hidden sm:inline'>
                  Drop a file or click to browse
                </span>
              </>
            )}
          </p>
          {trashButton && (
            <span
              role='presentation'
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            >
              {trashButton}
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          type='file'
          accept={accept}
          className='hidden'
          onChange={onInputChange}
        />
      </div>
      {message && <p className='text-[12px] text-red-600'>{message}</p>}
    </div>
  )
}
